import { useCallback, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { formatarNumero, type OrcamentoIaDto, type TotalIaDto } from '@nexital/shared';
import { useDependencias } from '../../application/dependencias';
import { useCarga } from './campos';

/** Nome de cada tipo de chamada (coluna finalidade da ia_chamada). */
const TIPOS: Record<string, string> = {
  extracao_edital: 'Leitura do edital',
  visao_itens: 'Imagens dos itens',
  vetores: 'Vetores dos trechos',
  identificacao: 'Identificação do arquivo',
  resposta: 'Resposta no chat',
  vetores_pergunta: 'Vetores da pergunta',
};

const dolar = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 4 });
const nomeDoMes = (mes: string) => new Date(`${mes}-15T12:00:00`).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });

function outroMes(mes: string, passo: number) {
  const [ano, m] = mes.split('-').map(Number) as [number, number];
  const d = new Date(Date.UTC(ano, m - 1 + passo, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

function Total({ titulo, dica, t }: { titulo: string; dica: string; t: TotalIaDto }) {
  return (
    <div className="config__envio-tipo">
      <span className="config__orcamento-titulo">{titulo}</span>
      <strong className="config__orcamento-valor">{dolar.format(t.custoUsd)}</strong>
      <small className="config__nota">
        {formatarNumero(t.chamadas)} chamadas · {formatarNumero(t.tokensEntrada)} tokens de entrada · {formatarNumero(t.tokensSaida)} de saída
      </small>
      <small className="config__nota">{dica}</small>
    </div>
  );
}

const LARGURA = 640;
const ALTURA = 220;
const MARGEM = { esquerda: 64, direita: 12, topo: 12, base: 28 };

/** Gasto acumulado por dia do mês, da empresa e do sistema, em linhas (SVG simples, sem biblioteca). */
function Grafico({ dias }: { dias: OrcamentoIaDto['dias'] }) {
  const maximo = Math.max(0.01, ...dias.map((d) => Math.max(d.empresa ?? 0, d.sistema ?? 0)));
  const largura = LARGURA - MARGEM.esquerda - MARGEM.direita;
  const altura = ALTURA - MARGEM.topo - MARGEM.base;
  const x = (i: number) => MARGEM.esquerda + (dias.length > 1 ? (i / (dias.length - 1)) * largura : 0);
  const y = (v: number) => MARGEM.topo + altura - (v / maximo) * altura;
  const linha = (serie: 'empresa' | 'sistema') =>
    dias.flatMap((d, i) => (d[serie] === null ? [] : [`${x(i).toFixed(1)},${y(d[serie]).toFixed(1)}`])).join(' ');
  const marcas = [0, 0.5, 1].map((f) => f * maximo);
  return (
    <figure className="config__orcamento-grafico">
      <svg viewBox={`0 0 ${LARGURA} ${ALTURA}`} role="img" aria-label="Gasto acumulado por dia, da empresa e do sistema">
        {marcas.map((v) => (
          <g key={v}>
            <line x1={MARGEM.esquerda} x2={LARGURA - MARGEM.direita} y1={y(v)} y2={y(v)} className="config__orcamento-guia" />
            <text x={MARGEM.esquerda - 6} y={y(v) + 4} textAnchor="end">
              {dolar.format(v)}
            </text>
          </g>
        ))}
        {dias.map((d, i) =>
          i === 0 || (i + 1) % 5 === 0 ? (
            <text key={d.dia} x={x(i)} y={ALTURA - 8} textAnchor="middle">
              {i + 1}
            </text>
          ) : null,
        )}
        <polyline points={linha('sistema')} className="config__orcamento-linha config__orcamento-linha--sistema" />
        <polyline points={linha('empresa')} className="config__orcamento-linha config__orcamento-linha--empresa" />
      </svg>
      <figcaption className="config__orcamento-legenda">
        <span className="config__orcamento-chave config__orcamento-chave--empresa">Empresa</span>
        <span className="config__orcamento-chave config__orcamento-chave--sistema">Sistema</span>
        <span>acumulado no mês, por dia</span>
      </figcaption>
    </figure>
  );
}

/**
 * Orçamento de IA (RQ-IA-021), só do superadministrador: o gasto do mês com IA da empresa (perguntas no chat e
 * identificação dos arquivos que ela envia) e o do sistema (leitura da base comum de editais), em dólares. Sem limite
 * nem aviso por enquanto.
 */
export function SecaoOrcamentoIa() {
  const { configuracoes } = useDependencias();
  const [mes, definirMes] = useState<string>();
  const carregar = useCallback(() => configuracoes.orcamentoIa({ mes }), [configuracoes, mes]);
  const { dados, erro } = useCarga(carregar);
  if (erro) return <p className="cartao-acesso__erro">{erro}</p>;
  if (!dados) return <p>Carregando…</p>;
  return (
    <div className="config__formulario">
      <div className="config__orcamento-barra">
        <button type="button" className="botao-icone" aria-label="Mês anterior" onClick={() => definirMes(outroMes(dados.mes, -1))}>
          <ChevronLeft size={18} />
        </button>
        <strong className="config__orcamento-mes">{nomeDoMes(dados.mes)}</strong>
        <button type="button" className="botao-icone" aria-label="Próximo mês" onClick={() => definirMes(outroMes(dados.mes, 1))}>
          <ChevronRight size={18} />
        </button>
      </div>
      <div className="config__envio-resumo">
        <Total titulo="Empresa" dica="Chamadas que alguém da empresa acionou." t={dados.empresa} />
        <Total titulo="Sistema" dica="Leitura dos editais da base comum, paga pela plataforma." t={dados.sistema} />
      </div>
      <Grafico dias={dados.dias} />
      {dados.porTipo.length === 0 ? (
        <p className="config__vazio">Nenhuma chamada de IA neste mês.</p>
      ) : (
        <table className="config__tabela">
          <thead>
            <tr>
              <th>Tipo de chamada</th>
              <th>Empresa</th>
              <th>Sistema</th>
            </tr>
          </thead>
          <tbody>
            {dados.porTipo.map((t) => (
              <tr key={t.finalidade}>
                <td>
                  {TIPOS[t.finalidade] ?? t.finalidade}
                  <small>{t.finalidade}</small>
                </td>
                <td>
                  {dolar.format(t.empresa.custoUsd)}
                  <small>{formatarNumero(t.empresa.chamadas)} chamadas</small>
                </td>
                <td>
                  {dolar.format(t.sistema.custoUsd)}
                  <small>{formatarNumero(t.sistema.chamadas)} chamadas</small>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
